import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { isNaN } from 'lodash';
import * as H from 'history';
import { DATE_FORMAT } from '@/app/const';
import { format as formatDate } from 'date-fns';
import {
  AnswerDataType,
  NextPayloadObject,
  PlayerStateHandler,
  QuestionDataType,
  QuestionPostType,
  QuizQuestion,
} from '@/modules/types/question';
import {
  Choice,
  ChoiceDataType,
  ChoiceMetadataType,
  MatchChoice,
  QuestionResponseType,
} from '@/modules/types/choice';
import { RepositionEntityType } from '@/modules/types/helper';
import {
  ElearningDataType,
  ElearningStatus,
  PublishStatus,
  StatusMetadataType,
} from '@/modules/types/elearning';
import config from '@/lib/config';
import {
  CkFinderResourceTypeEnum,
  CKConfigType,
  CKModuleType,
  CKFileKindType,
} from '@/modules/types/ckFinder';
import { ThemeDataType } from '@/modules/types/theme';
import packageJson from '../../../package.json';
import { FrameDataType, VocamMetadataType } from '@/modules/types/frame';
import { ElementType } from '@/modules/types/element';
/**
 * Get data from Localstorage
 */
export function getLocalstorage(
  key: string,
  format: 'string' | 'object' = 'string',
): any {
  try {
    const localStorageData = localStorage.getItem(key);
    if (localStorageData) {
      if (format === 'string') {
        return localStorageData;
      }
      if (format === 'object') {
        try {
          return JSON.parse(localStorageData);
        } catch (e) {
          return false;
        }
      }
    }
    return false;
  } catch (error) {
    // TO-DO: Log Error
  }

  return false;
}

/**
 * Set data to Localstorage
 */
export function setLocalstorage(
  key: string,
  data: any,
  format: 'string' | 'object' = 'string',
) {
  try {
    if (localStorage) {
      if (format === 'string') {
        localStorage.setItem(key, data);
      }
      if (format === 'object') {
        localStorage.setItem(key, JSON.stringify(data));
      }
    }
  } catch (error) {
    // TO-DO: Log Error
  }
}

export const dompurifyConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
};

export const sanitizeHTML = (html: string, customconfig?: any) =>
  DOMPurify.sanitize(html, { ...dompurifyConfig, ...customconfig });

export const ScrollbarIndicator = () => {
  const elementRef = useRef<HTMLDivElement>(null);
  setTimeout(() => {
    if (
      elementRef.current &&
      elementRef.current.scrollHeight > elementRef.current.clientHeight
    ) {
      elementRef.current.classList.add('scrollbar-indicator');
      elementRef.current.addEventListener('scroll', () => {
        if (elementRef.current) {
          elementRef.current.classList.add('scrollbar-indicator--hidden');
        }
      });
    }
  }, 10); // Need time delay til the dom is ready

  return elementRef;
};

export function eventDispatcher(key: string, data?: Record<string, unknown>) {
  const customEvent = new CustomEvent(key, data);
  document.dispatchEvent(customEvent);
}

/**
 * A simple queryString helper
 */
interface QueryStringInterface {
  parse: (query: string) => { [key: string]: any };
  stringify: (params: any) => string;
}

export const queryString: QueryStringInterface = {
  parse: (query: string) => {
    const params: { [key: string]: any } = {};
    if (query) {
      const qs = new URLSearchParams(query);
      qs.forEach((value, key) => {
        params[key] = qs.get(key);
      });
    }
    return params;
  },
  stringify: (params: { [key: string]: any }) => {
    const qs = new URLSearchParams('');
    if (params) {
      const keys = Object.keys(params);
      if (keys && Array.isArray(keys)) {
        keys.map((key: any) => {
          qs.append(key, params[key]);
          return false;
        });
      }
    }

    return qs.toString();
  },
};

function numberParser(num: any, defaultNumber: number) {
  const parsedInt = parseInt(num, 10);
  return isNaN(parsedInt) ? defaultNumber : parsedInt;
}

export function getQueryString(
  locationSearch?: string,
  defaultQuery?: { [key: string]: any },
) {
  const currentQuery = queryString.parse(
    locationSearch || window.location.search,
  );
  const query = {
    ...defaultQuery,
    ...currentQuery,
  };

  if (query.page) {
    query.page = numberParser(
      query.page,
      (defaultQuery && defaultQuery.page) || 1,
    );
  }
  if (query.limit) {
    query.limit = numberParser(
      query.limit,
      (defaultQuery && defaultQuery.limit) || 1,
    );
  }

  return query;
}

export const pushParamsToUrl = (
  params: { [key: string]: any },
  history: H.History,
) => {
  const queryParam = getQueryString();
  const combinedParam = {
    ...queryParam,
    ...params,
  };

  history.push({
    pathname: history.location.pathname,
    search: `?${queryString.stringify(combinedParam)}`,
  });
};

export function invertColorBW(hex: string) {
  const hexColorRegex = /#(([0-9a-fA-F]{2}){3,4}|([0-9a-fA-F]){3,4})/g;

  if (!hexColorRegex.test(hex)) {
    return '';
  }

  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
}

export const createFormattedDateString = (date: Date) =>
  formatDate(new Date(date), DATE_FORMAT);

export const conditionalOutputHelper = (
  condition: boolean,
  first: any,
  second: any,
) => (condition ? first : second);

export const replaceContentAssetLink = ({
  content,
  newLink,
  tenantId,
  elearningId,
}: {
  content: string;
  newLink: string;
  tenantId: string;
  elearningId: string;
}): string => {
  const contentRegex = new RegExp(
    `/content/${tenantId}/elearnings/${elearningId}`,
    'g',
  );

  return content?.replace(contentRegex, newLink);
};

export interface EntityLocationInterface {
  tenantId: string;
  entity: 'elearnings' | 'quizzes' | 'themes';
  entityId: string;
}

export const getEntityLocation = ({
  tenantId,
  entity,
  entityId,
}: EntityLocationInterface) => `/content/${tenantId}/${entity}/${entityId}`;

export interface PublishedEntityLocationInterface {
  tenantId: string;
  type: 'review' | 'live' | 'scorm' | 'breathe';
  entityId: string;
  identifier?: number;
  entity: 'elearnings' | 'quizzes' | 'themes';
  contentType: 'published-content-assets' | 'published-content';
}

export const getPublishedEntityLocation = ({
  tenantId,
  type,
  entityId,
  identifier,
  entity,
  contentType,
}: PublishedEntityLocationInterface): string =>
  `/${contentType}/${tenantId}/${type}/${entity}/${entityId}/${identifier}`;

export const isElearningPublishedToSubdomain = (
  elearningStatusMetadata: StatusMetadataType,
  subdomain: string,
) =>
  elearningStatusMetadata.publishedTo !== undefined &&
  elearningStatusMetadata.publishedTo.includes(subdomain);

export const isElearningPublishedToReview = (elearningPublishStatus: number) =>
  elearningPublishStatus >= ElearningStatus.Review;

export const isElearningPublishedToLive = (elearningLmsStatus: number) =>
  elearningLmsStatus > PublishStatus.unpublished;

export const isElearningPublishedToLiveNew = (
  elearningStatusMetadata: StatusMetadataType,
) =>
  elearningStatusMetadata.publishedToLive !== undefined &&
  elearningStatusMetadata.publishedToLive === PublishStatus.published;

export const canPreviewLive = (elearningStatusMetadata: StatusMetadataType) =>
  isElearningPublishedToLiveNew(elearningStatusMetadata) ||
  isElearningPublishedToLive(elearningStatusMetadata.lmsStatus);

export const isElearningPublishedToReviewNew = (
  elearningStatusMetadata: StatusMetadataType,
) =>
  elearningStatusMetadata.publishedToReview !== undefined &&
  elearningStatusMetadata.publishedToReview === PublishStatus.published;

export const isElearningPublishedToBreathe = (
  elearningStatusMetadata: StatusMetadataType,
) =>
  elearningStatusMetadata.breatheStatus !== undefined &&
  elearningStatusMetadata.breatheStatus === PublishStatus.published;

const getChoiceType = (choices: ChoiceDataType[], type: 1 | 2) =>
  choices
    .filter((choice) => choice.choiceMetadata?.type === type)
    .sort(
      (a, b) =>
        (a.choiceMetadata?.position || 0) - (b.choiceMetadata?.position || 0),
    );

const checkMatchDnDAnswer = (
  question: QuestionDataType,
  sourceAnswer: { [key: string]: string },
) => {
  /* Since Match does not rely on isCorrect property - we do not need to check for useUUID */
  /* Checking for type & position - which does not matter if we use lcmsId or choiceId (unless the PLAYER passed the lcmsIds which it does not - confirmed & checked inCode) */
  /* data structure would be {'item':'target'} */

  const choiceItems = getChoiceType(
    question.choices,
    ChoiceMetadataType.main,
  ).map((choice) => choice.id);
  const choiceTargets = getChoiceType(
    question.choices,
    ChoiceMetadataType.secondary,
  ).map((choice) => choice.id);

  const sourceItems = Object.keys(sourceAnswer);

  for (let index = 0; index < choiceItems.length; index += 1) {
    const choiceItem = choiceItems[index];
    const choiceTarget = choiceTargets[index];

    const hasItemKey = sourceItems.includes(choiceItem);
    const hasCorrectKeyPair = sourceAnswer[choiceItem] === choiceTarget;

    if (!hasItemKey) return false;
    if (!hasCorrectKeyPair) return false;
  }

  return true;
};

export const getMultiAnswer = (
  question: QuestionDataType,
  disableIsCorrectFilter?: boolean,
) => {
  const lcmsIdAnswer = question.multiAnswer || `${question.answer}`;
  const hasUseUuidChoice =
    question.choices.filter((choice) => choice.choiceMetadata?.useUUID).length >
    0;

  return !hasUseUuidChoice
    ? lcmsIdAnswer.split(',')
    : question.choices
        .filter(
          (choice) =>
            disableIsCorrectFilter || choice.choiceMetadata?.isCorrect,
        )
        .sort(
          (a, b) =>
            (a.choiceMetadata?.position || 0) -
            (b.choiceMetadata?.position || 0),
        )
        .map((choice) =>
          choice.choiceMetadata?.useUUID
            ? choice.id
            : `${choice.lcmsReferenceIds?.id}`,
        );
};

const checkMultipleAnswers = (
  question: QuestionDataType,
  sourceAnswer: string[],
) => {
  const targetAnswer = getMultiAnswer(question);

  const sortedSourceAnswer = sourceAnswer.sort().join(',');
  const sortedTargetAnswer = targetAnswer.sort().join(',');

  return sortedSourceAnswer === sortedTargetAnswer;
};

const checkSortedAnswers = (
  question: QuestionDataType,
  sourceAnswer: string[],
) => {
  const targetAnswer = getMultiAnswer(question, true);

  const sortedSourceAnswer = sourceAnswer.join(',');
  const sortedTargetAnswer = targetAnswer.join(',');

  return sortedSourceAnswer === sortedTargetAnswer;
};

export const getAnswerUserResponse = (answer: AnswerDataType): string => {
  const { answer: trueOrFalseAnswer, multiAnswer, matchKeyPair } = answer;

  if (trueOrFalseAnswer !== undefined) return `${trueOrFalseAnswer}`;
  if (multiAnswer) return multiAnswer.join(',');
  if (matchKeyPair) return JSON.stringify(matchKeyPair);
  return '';
};

export const getCorrectChoices = (
  question: QuestionDataType,
): ChoiceDataType[] => {
  // @todo: refactor this function to remove use of lcmsReferenceIds
  const lcmsIdAnswer = question.multiAnswer || `${question.answer}`;
  const lcmsAnswersArray = lcmsIdAnswer.split(',');

  switch (question.questionMetadata.type) {
    case QuestionResponseType.trueOrFalse:
      return [];
    case QuestionResponseType.singleAnswer:
    case QuestionResponseType.trafficLight:
    case QuestionResponseType.multiCorrect:
      return question.choices
        .map((choice) => choice)
        .filter(
          (choice) =>
            (!choice.choiceMetadata?.useUUID &&
              lcmsAnswersArray.includes(`${choice.lcmsReferenceIds?.id}`)) ||
            choice.choiceMetadata?.isCorrect,
        );
    case QuestionResponseType.order:
      return question.choices
        .map((choice) => choice)
        .sort(
          (a, b) =>
            (a.choiceMetadata?.position || 0) -
            (b.choiceMetadata?.position || 0),
        );
    case QuestionResponseType.match:
      return question.choices.map((choice) => choice);
    default:
      return [];
  }
};

export const isValidAnswer = (
  question: QuestionDataType,
  answer: AnswerDataType,
): boolean => {
  if (question.questionMetadata.type !== answer.questionMetadata.type) {
    return false;
  }

  switch (answer.questionMetadata.type) {
    case QuestionResponseType.trueOrFalse:
      return answer.answer !== undefined
        ? +question.answer === +answer.answer
        : false;
    case QuestionResponseType.singleAnswer:
    case QuestionResponseType.trafficLight:
    case QuestionResponseType.multiCorrect:
      return answer.multiAnswer
        ? checkMultipleAnswers(question, answer.multiAnswer)
        : false;
    case QuestionResponseType.order:
      return answer.multiAnswer
        ? checkSortedAnswers(question, answer.multiAnswer)
        : false;
    case QuestionResponseType.match:
      return answer.matchKeyPair
        ? checkMatchDnDAnswer(question, answer.matchKeyPair)
        : false;
    default:
      return false;
  }
};

const getRandomIntFromTo = (min: number, max: number) =>
  Math.round(Math.random() * max + min);

export const getEnabledQuestions = (questions: QuestionDataType[]) =>
  questions.filter(({ questionMetadata }) => !questionMetadata.disabled);

export const getCompulsoryQuestions = (
  questions: QuestionDataType[],
): QuestionDataType[] =>
  questions.filter(
    ({ questionMetadata }) =>
      +questionMetadata.compulsory === 1 && +questionMetadata.disabled === 0,
  );

export const getRandomQuestion = (
  questions: QuestionDataType[],
): QuestionDataType => {
  if (questions.length === 1) return questions[0];

  // filter compulsory questions
  const compulsoryQuestions = getCompulsoryQuestions(questions);
  // filter remaining not disabled questions
  const remainingQuestions = questions.filter(
    ({ questionMetadata }) =>
      questionMetadata.compulsory === 0 && questionMetadata.disabled === 0,
  );

  if (compulsoryQuestions.length > 0)
    return compulsoryQuestions[
      getRandomIntFromTo(0, compulsoryQuestions.length - 1)
    ];

  return remainingQuestions[
    getRandomIntFromTo(0, remainingQuestions.length - 1)
  ];
};

const revalidatePosition = (choices: Choice[]): Choice[] => {
  let itemPosition = 0;
  let targetPosition = 0;

  const reorderPositionChoices = choices
    .sort((first, second) => first.position - second.position)
    .map((choice) => {
      if (choice.type === 1) {
        choice.position = itemPosition;
        itemPosition += 1;
      } else {
        choice.position = targetPosition;
        targetPosition += 1;
      }

      return choice;
    });

  return reorderPositionChoices;
};

export const sortMatchChoices = (choices: Choice[]): Choice[] => {
  const reorderPositionChoices = revalidatePosition(choices);

  const sortedChoices: Choice[] = reorderPositionChoices.map((item) => item);

  /* Reorders match item and target choices using position and type to correct indexes for useForm single array */
  for (let index = 0; index < reorderPositionChoices.length; index += 1) {
    const element = reorderPositionChoices[index];
    /* adjust type 1 position to accomodate for type 2 */
    /* position type 2 target choice 1 index higher with its type 1 item pair */
    const i = element.position * 2 + (element.type === 1 ? 0 : 1);

    sortedChoices.splice(i, 1, element);
  }

  return sortedChoices;
};

export const convertQuizQuestionToQuestionDataType = (
  data: QuizQuestion,
): QuestionPostType => ({
  ...(data.id
    ? {
        id: data.id,
      }
    : {}),
  content: data.question,
  answer: data?.answer || 0,
  multiAnswer: data.multiAnswer?.toString() || '',
  lcmsReferenceIds: {},
  questionMetadata: {
    compulsory: +data.compulsory,
    disabled: +data.disabled,
    type: +data.responseType,
    position: +(data.position || 0),
    img: data.image,
    sound: data.sound,
  },
  feedbackMetadata: {
    feedback1: data.correctfeedback,
    feedback2: data.incorrectfeedback,
  },
});

/* this function operates under the assumption that 
entities are already ordered/sorted by their position when passed here
- This is so that we can accomodate for all entity types
regarless of where or how deep we nest there position data
 */
export const movePositionValueUp = (
  sortedEntities: { id: string }[],
  sourceIndex: number,
  destinationIndex: number,
): RepositionEntityType[] => {
  if (sourceIndex < destinationIndex) return [];

  const repositionQuestions: RepositionEntityType[] = [];

  for (let i = 0; destinationIndex + i < sourceIndex; i += 1) {
    repositionQuestions.push({
      id: sortedEntities[destinationIndex + i].id,
      position: destinationIndex + i + 1,
    });
  }

  return repositionQuestions;
};

/* this function operates under the assumption that 
entities are already ordered/sorted by their position when passed here
- This is so that we can accomodate for all entity types
regarless of where or how deep we nest there position data
 */
export const movePositionValueDown = (
  sortedEntities: { id: string }[],
  sourceIndex: number,
  destinationIndex: number,
): RepositionEntityType[] => {
  if (sourceIndex > destinationIndex) return [];

  const repositionQuestions: RepositionEntityType[] = [];

  for (let i = 0; destinationIndex + i > sourceIndex; i -= 1) {
    repositionQuestions.push({
      id: sortedEntities[destinationIndex + i].id,
      position: destinationIndex + (i - 1),
    });
  }

  return repositionQuestions;
};

/* this function operates under the assumption that 
entities are already ordered/sorted by their position when passed here
- This is so that we can accomodate for all entity types
regarless of where or how deep we nest there position data
 */
export const UpdateEntityPositions = (
  sortedEntities: { id: string }[],
  sourceIndex: number,
  destinationIndex: number,
): RepositionEntityType[] => {
  if (!sortedEntities || sortedEntities.length <= 0) return [];

  const repositionQuestions: RepositionEntityType[] = [];

  repositionQuestions.push({
    id: sortedEntities[sourceIndex].id,
    position: destinationIndex,
  });

  const repositionUpResult = movePositionValueUp(
    sortedEntities,
    sourceIndex,
    destinationIndex,
  );
  const repositionDownResult = movePositionValueDown(
    sortedEntities,
    sourceIndex,
    destinationIndex,
  );

  const allRepositionedEntities = repositionQuestions
    .concat(repositionUpResult)
    .concat(repositionDownResult);

  return allRepositionedEntities;
};

/* determine choice isCorrect value based on LCMS reference ids */
export const getIsCorrectLcms = (
  multiAnswer: string[],
  choice: ChoiceDataType,
  answer: number,
): boolean =>
  multiAnswer.length > 0
    ? multiAnswer.includes(choice.lcmsReferenceIds.id)
    : +choice.lcmsReferenceIds.id === answer;

/* determine choice isCorrect value based on both current LCMS reference ids or new if useUUID is true */
export const determineIsCorrect = (
  multiAnswer: string[],
  choice: ChoiceDataType,
  answer: number,
): boolean => {
  if (!choice.choiceMetadata && !choice.lcmsReferenceIds) return false;

  if (!choice.choiceMetadata || !choice?.choiceMetadata.useUUID)
    return getIsCorrectLcms(multiAnswer, choice, answer);

  /* used double quotation to make sure we are returning strict boleean type value regardless 
  because typescript has a tendency to lie in terms of type */
  return !!choice.choiceMetadata.isCorrect;
};

export const convertChoicesDataTypeToFormChoice = (
  choices: ChoiceDataType[],
  question: QuestionDataType,
  allowSort: boolean,
): Choice[] => {
  const choicesDuplicate = choices.map((choice) => {
    const answer = +question.answer || 0;
    const multiAnswer = question.multiAnswer?.split(',') || [];
    const isCorrect = determineIsCorrect(multiAnswer, choice, answer);

    return {
      id: choice.id,
      choiceId: choice.id,
      content: choice.content || '',
      position: choice.choiceMetadata?.position as number,
      type: (choice.choiceMetadata?.type as number) || 1,
      disabled: choice.choiceMetadata?.disabled as number,
      isCorrect,
      useUUID: (choice.choiceMetadata?.useUUID as boolean) || false,
    };
  });

  if (!allowSort)
    return choicesDuplicate.sort((a, b) => a.position - b.position);

  return sortMatchChoices(choicesDuplicate);
};

export const convertQuestionDataTypeToQuizQuestion = (
  question: QuestionDataType,
  choicesData: ChoiceDataType[] | undefined,
  matchResponseType: number,
  trueOrFalseResponseType: number,
): QuizQuestion => {
  /* Sort position of choice for match type questions */
  const isMatchSorted = question.questionMetadata.type === matchResponseType;

  const choices = !choicesData
    ? []
    : convertChoicesDataTypeToFormChoice(choicesData, question, isMatchSorted);

  return {
    ...(question.id && { id: question.id }),
    question: question.content,
    responseType: question.questionMetadata.type || trueOrFalseResponseType,
    compulsory: +question.questionMetadata.compulsory,
    disabled: +question.questionMetadata.disabled,
    position: +(question.questionMetadata.position || 0),
    multiAnswer: question.multiAnswer?.split(',') || [],
    answer: +question.answer || 0,
    choices: choices || [],
    correctfeedback: question.feedbackMetadata.feedback1,
    incorrectfeedback: question.feedbackMetadata.feedback2,
    image: question.questionMetadata.img ?? '',
    sound: question.questionMetadata.sound ?? '',
  };
};

export const transformChoicesToMatchChoices = (
  choicesToTransform: Choice[],
): MatchChoice[] => {
  const matchChoices: MatchChoice[] = [];

  const choiceItems = choicesToTransform
    .map((choice) => choice)
    .filter((choice) => +choice.type === ChoiceMetadataType.main)
    .sort((first, second) => first.position - second.position);

  choiceItems.forEach((choice) => {
    const target = choicesToTransform.find(
      (targetChoice) =>
        +targetChoice.type === ChoiceMetadataType.secondary &&
        targetChoice.position === choice.position,
    );

    if (target) {
      matchChoices.push({
        item: choice,
        target,
      });
    }
  });

  return matchChoices;
};

export const isCompulsoryQuestionCountEnough = ({
  offset,
  questionAsked,
  compulsoryQuestionCount,
}: {
  offset: number;
  questionAsked: number;
  compulsoryQuestionCount: number;
}): boolean => {
  if (questionAsked <= 0) return false;

  const totalQuestionCount = compulsoryQuestionCount + offset;

  return totalQuestionCount <= questionAsked;
};

export const isEnabledQuestionCountEnough = ({
  offset,
  questionAsked,
  enabledQuestionCount,
}: {
  offset: number;
  questionAsked: number;
  enabledQuestionCount: number;
}): boolean => {
  if (questionAsked <= 0) return false;

  const totalQuestionCount = enabledQuestionCount + offset;

  return totalQuestionCount >= questionAsked;
};

export const generateReviewUrl = (
  elearning: ElearningDataType,
  oldUrl: string,
  oldToken: string,
) => {
  const elearningPublishStatus = elearning.statusMetadata;
  const elearningLcmsReferenceIds = elearning.lcmsReferenceIds;

  return !elearningPublishStatus.publishedToReview
    ? `${oldUrl}/learning_modules/${elearningLcmsReferenceIds?.companyId}/course_${elearningLcmsReferenceIds?.id}_${elearningLcmsReferenceIds?.encryptedId}/index.php?t=${oldToken}`
    : `/preview-review/${elearning.tenantId}/${elearning.id}/review`;
};

export const generateLiveUrl = (
  elearning: ElearningDataType,
  oldUrl: string,
  oldToken: string,
) => {
  const elearningPublishStatus = elearning.statusMetadata;
  const elearningLcmsReferenceIds = elearning.lcmsReferenceIds;

  return !elearningPublishStatus.publishedToLive
    ? `${oldUrl}/lms_modules/${elearningLcmsReferenceIds?.companyId}/course_${elearningLcmsReferenceIds?.id}_${elearningLcmsReferenceIds?.encryptedId}/index.php?t=${oldToken}`
    : `/preview-live/${elearning.tenantId}/${elearning.id}/live`;
};

export const openPlayerUIWindow = (
  elearning: ElearningDataType,
  url: string,
  target: string,
  defaultWindowMetadata: {
    height: number;
    width: number;
    windowBgColor: string;
  },
) => {
  const windowMetadata = elearning.theme
    ? elearning.theme.windowMetadata
    : defaultWindowMetadata;

  // window.open sizes the OUTER window; keep the inner viewport at or above
  // the theme width so the preview never lands in the <=1023px tablet band
  window.open(
    url,
    target,
    `width=${windowMetadata.width + 45},height=${
      windowMetadata.height + 10
    }, location=0,status=0, scrollbars=0, toolbar=0,menubar=0,resizable=1`,
  );
};

export const processQuestion = (
  answer: AnswerDataType,
  playerStateHandler: PlayerStateHandler,
): NextPayloadObject | undefined => {
  playerStateHandler.questionsAsked += 1;
  const lastQuestion =
    playerStateHandler.questionsToAsk <= playerStateHandler.questionsAsked;

  const currentQuestion = playerStateHandler.modifiedQuestions.find(
    (question) => question.id === answer.questionId,
  );

  if (!currentQuestion) return undefined;

  playerStateHandler.modifiedQuestions =
    playerStateHandler.modifiedQuestions.filter(
      (question) => question.id !== currentQuestion.id,
    );

  const isValidatedAnswer = isValidAnswer(currentQuestion, answer);
  playerStateHandler.correctAnswers += isValidatedAnswer ? 1 : 0;

  const getAnswerAsString = getAnswerUserResponse(answer);
  playerStateHandler.questionsCompleted.push({
    questionId: currentQuestion.id,
    userResponse: getAnswerAsString,
    isCorrect: isValidatedAnswer,
  });

  return {
    feedback: currentQuestion?.feedbackMetadata,
    isValidatedAnswer,
    correctAnswer: getCorrectChoices(currentQuestion),
    lastQuestion,
    ...(lastQuestion
      ? {}
      : {
          nextQuestion: getRandomQuestion(playerStateHandler.modifiedQuestions),
        }),
  };
};

export const applyEmbbedStyleToPlayer = (
  options: any,
  videoMount: HTMLElement,
  videoNode: React.RefObject<HTMLVideoElement>,
) => {
  if (!videoNode.current) return;

  const { style } = options;

  if (style.includes('position')) {
    const { offsetLeft, offsetTop } = videoMount;
    const cssStyling = `background-color:transparent; z-index: 1; left: ${offsetLeft}px; top: ${offsetTop}px;`;
    videoNode.current.style.cssText = cssStyling;
    if (videoMount.parentElement) {
      videoMount.parentElement.appendChild(videoNode.current);
    }
  } else {
    const cssStyling = `background-color:transparent; z-index: 1; left: 0; top: 0;`;
    const vjsWrapper = document.createElement('div');
    vjsWrapper.classList.add('vjs-wrapper');
    vjsWrapper.style.cssText = cssStyling;
    vjsWrapper.appendChild(videoNode.current);
    if (videoMount.parentElement) {
      videoMount.parentElement.style.position = 'relative';
      videoMount.parentElement.appendChild(vjsWrapper);

      const srcAttribute = videoNode.current.getAttribute('src');

      /* Query placeholder we need to replace w/ actual embedded video */
      const imgPlaceholder = videoMount.parentElement.querySelector(
        `[data-src="${srcAttribute}"][name="custom_media_embed"]`,
      );

      if (imgPlaceholder !== null) {
        /* Copy the css from placeholder to actual video */
        videoNode.current.style.cssText = (
          imgPlaceholder as HTMLElement
        )?.style?.cssText;
        /* Remove placeholder element - this is to prevent override of exact same placeholder if 2 exact same videos are embedded. */
        imgPlaceholder.remove();
      }
      videoNode.current.style.visibility = 'visible';
      videoNode.current.style.display = 'block';
    }
  }
};

export const getCkFileType = (fileTypes: CkFinderResourceTypeEnum[]): string =>
  (fileTypes ?? []).map((item) => `&type=${item}`).join('');

export const getCKFileName = (filePath: string, segment: string): string => {
  if (!filePath) return '';
  if (filePath.endsWith('/')) return '';
  if (!filePath.includes(segment)) return filePath;

  const startIndex = filePath.indexOf(segment) + segment.length;
  const endIndex = startIndex + filePath.length;

  const fileName = filePath.substring(startIndex, endIndex);

  return fileName;
};

export const getCkFinderConfig = ({
  type,
  tenantId,
  entityId,
  folderName,
}: {
  type?: string;
  tenantId: string;
  entityId: string;
  folderName: CKModuleType;
}): CKConfigType => {
  const configType = {
    connectorInfo: `basedir=${tenantId}&path=${folderName}/${entityId}/pages/${
      type ?? ''
    }`,
    connectorPath: config.ckFinderConnectorPath,
  };

  return configType;
};

export const getCKfinderTemplateImageConfig = (
  tenantId: string,
): CKConfigType => {
  const configType = {
    connectorInfo: `basedir=${tenantId}&path=content/media/slideTemplate/images`,
    connectorPath: config.ckFinderConnectorPath,
  };

  return configType;
};

export const getCkEditorConfig = ({
  tenantId,
  type,
  elearningId,
  clipboardHandleImages = false,
}: {
  tenantId: string;
  elearningId: string;
  type: CKModuleType;
  clipboardHandleImages?: boolean;
}) => {
  /* Final path will be one of the following: */
  /* Elearning -> ${tenantId}/elearnings/${elearningId}/pages/ */
  /* Quiz -> ${tenantId}/quizzes/${elearningId}/pages/ */
  /* Themes -> ${tenantId}/themes/${themeId}/pages/ */
  const connectorUrlQueryParam = `basedir=${tenantId}&path=${type}/${elearningId}/pages/`;

  return {
    filebrowserUploadUrl: `${config.ckFinderConnectorPath}?${connectorUrlQueryParam}`,
    filebrowserImageUploadUrl: `${config.ckFinderConnectorPath}?${connectorUrlQueryParam}`,
    filebrowserBrowseUrl: `/static/V${packageJson.version}/ckfinder/ckfinder.html?${connectorUrlQueryParam}`,
    filebrowserImageBrowseUrl: `/static/V${packageJson.version}/ckfinder/ckfinder.html?${connectorUrlQueryParam}`,
    clipboard_handleImages: clipboardHandleImages,
  };
};

export const getAssetLocation = ({
  tenantId,
  entityId,
  entity,
  fileType,
  fileName,
}: {
  tenantId: string;
  entityId: string;
  entity: CKModuleType;
  fileType: CKFileKindType;
  fileName: string;
}) =>
  `/content/${tenantId}/${entity}/${entityId}/pages/${fileType}/${fileName}`;

/**
 * Published data is stamped with an assetBasePath (see publishedEntities.ts)
 * pointing at /published-content-assets/...; draft data is not, so it falls
 * back to the /content/ authoring path.
 */
export const resolveAssetUrl = ({
  assetBasePath,
  ...assetLocation
}: { assetBasePath?: string } & Parameters<typeof getAssetLocation>[0]) => {
  // Draft metadata occasionally holds an absolute path or full URL rather
  // than a bare filename (same shape resources.ts and getCKFileName guard
  // against) — prefixing would mangle it, so pass it through.
  // Deliberately draft-only: stamped data must always resolve under the
  // published base and never back to /content/. A stamped absolute value
  // still yields a doubled, broken URL — unchanged from before this fix,
  // since getAssetLocation prefixed it too. That is bad data rather than
  // bad resolution; it is part of the Phase 4 legacy-path audit.
  if (
    !assetBasePath &&
    (assetLocation.fileName?.startsWith('/') ||
      assetLocation.fileName?.includes('://'))
  ) {
    return assetLocation.fileName;
  }

  return assetBasePath
    ? `${assetBasePath}/pages/${assetLocation.fileType}/${assetLocation.fileName}`
    : getAssetLocation(assetLocation);
};

export const addBackgroundToContent = ({
  content,
  mobileContent,
  elearningId,
  tenantId,
  bgVideo,
}: {
  content: string;
  mobileContent: string;
  elearningId: string;
  tenantId: string;
  bgVideo: string;
}) => {
  const vidRegex = /<video[^>]*id="bgVideo"[^>]*>.*?<\/video>/gis;

  const sanitizedContent = content?.replace(vidRegex, '');
  const sanitizedMobileContent = mobileContent?.replace(vidRegex, '');
  const bgVideoFileName = getCKFileName(bgVideo ?? '', '/videos/');

  if (bgVideo && bgVideo !== '') {
    return {
      sanitizedContent: `
        <video id="bgVideo" autoPlay muted loop style='position:absolute;inset: 0;width:100%;'>
          <source
            src='${getAssetLocation({
              tenantId,
              entity: 'elearnings',
              entityId: elearningId,
              fileType: 'videos',
              fileName: bgVideoFileName,
            })}'
            type='${`video/${bgVideo.split('.').pop()}`}'
          />
          Your browser does not support HTML5 video.
        </video>
        ${sanitizedContent}
        `,
      sanitizedMobileContent: `
        <video id="bgVideo" autoPlay muted loop style='position:absolute;inset: 0;width:100%;'>
          <source
            src='${getAssetLocation({
              tenantId,
              entity: 'elearnings',
              entityId: elearningId,
              fileType: 'videos',
              fileName: bgVideoFileName,
            })}'
            type='${`video/${bgVideo.split('.').pop()}`}'
          />
          Your browser does not support HTML5 video.
        </video>
        ${sanitizedMobileContent}
        `,
    };
  }

  return {
    sanitizedContent,
    sanitizedMobileContent,
  };
};

/**
 * Detects if the device is touch-only (no hover capability).
 * Uses the (hover: none) media query: true on phones/tablets, false on desktops with mouse.
 * Safe for SSR (returns false when window is undefined).
 */
export const isTouchOnlyDevice = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(hover: none)').matches;
};

/**
 * Hook that returns whether the device is touch-only (no hover).
 * Updates on resize/orientation change (e.g. docking a tablet).
 */
export const useIsTouchOnlyDevice = (): boolean => {
  const [isTouchOnly, setIsTouchOnly] = useState(() => isTouchOnlyDevice());

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none)');
    const handleChange = () => setIsTouchOnly(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isTouchOnly;
};

/* custom hook to detect isMobile breakpoint based on screensize NOT device */
export const useIsMobileScreenSize = (breakpoint = 504) => {
  const [screenSize, setScreenSize] = useState<{
    width: number;
    height: number;
  }>({
    // Changing this to use the innerWidth since screen.width is getting the actual browser size and not the screen size
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isMobile, setIsMobile] = useState<boolean>(
    window.innerWidth <= breakpoint,
  );
  const [isPortrait, setIsPortrait] = useState<boolean>(
    window.innerWidth < window.innerHeight,
  );

  const onOrientationChange = (event: Event | any) => {
    if (!event.currentTarget) return;

    const { width, height } = event.currentTarget.screen ?? screenSize;
    setScreenSize({ width, height });
    setIsPortrait(height > width);
  };

  useEffect(() => {
    window.addEventListener('orientationchange', onOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, []);

  useEffect(() => {
    setIsMobile(
      screenSize.width <= breakpoint ||
        (!isPortrait && screenSize.height <= breakpoint),
    );
  }, [breakpoint, isPortrait, screenSize]);

  return { screenSize, isMobile, isPortrait };
};

export const transformElearningThemeAssetLinks = (
  theme: ThemeDataType,
  prefix: string,
) => {
  const bgImg = theme?.bgImg ? `${prefix}/${theme.bgImg}` : '';
  const banner = theme?.banner ? `${prefix}/${theme.banner}` : '';

  return { ...theme, bgImg, banner };
};

export const getValidPage = (
  currentPageSize: number,
  newPageSize: number,
  pageNumber: number,
  totalItems: number,
): number => {
  const rowNumber = (pageNumber - 1) * currentPageSize + 1;
  const ratio = rowNumber / totalItems;

  pageNumber = Math.ceil((totalItems / newPageSize) * ratio);

  return pageNumber;
};

export const addOrRemoveFromArray = (
  arr: string[],
  element: string,
): string[] => {
  const index = arr.indexOf(element);

  if (index !== -1) {
    arr.splice(index, 1);
  } else {
    arr.push(element);
  }

  return arr;
};

export const removeHashOnHex = (hex: string): string =>
  hex.startsWith('#') ? hex.slice(1) : hex;

export const addHashOnHex = (hex: string): string =>
  hex.startsWith('#') ? hex : `#${hex}`;

export const stripHTML = (content: string): string =>
  content?.replace(/(<([^>]+)>)/gi, '');

export const sanitizeObject = (content: object): object => {
  const sanitizedObject: any = {};
  Object.entries(content).forEach(([key, value]) => {
    if (value !== '' && value != null) {
      sanitizedObject[key] = value;
    }
  });
  return sanitizedObject;
};

export const getContentTypeResponse = (response: Response) => {
  const contentType = response.headers.get('content-type');
  switch (true) {
    case contentType?.includes('text'):
      return response.text();

    case contentType?.includes('json'):
      return response.json();

    case contentType?.includes('form'):
      return response.formData();

    default:
      return response.blob();
  }
};

export const hasValidVocamDataIds = (vocamMetadata: VocamMetadataType) =>
  !!vocamMetadata &&
  !!vocamMetadata.regionId &&
  !!vocamMetadata.channelId &&
  !!vocamMetadata.moduleId &&
  !!vocamMetadata.topicId;

export const hasVocamData = (frameData?: FrameDataType) =>
  !!frameData && hasValidVocamDataIds(frameData.vocamMetadata);

export const getCurrentFrame = (frames: FrameDataType[], frameId: string) =>
  frames.find((frame: FrameDataType) => frame.id === frameId);

export const getNewFramePosition = (framesData: FrameDataType[]) => {
  if (framesData?.length) {
    const lastFrame = framesData[framesData.length - 1];
    const newPosition =
      lastFrame.frameMetadata && lastFrame.frameMetadata.position
        ? lastFrame.frameMetadata.position + 1
        : framesData.length;
    return newPosition;
  }
  return 0;
};

export const getModuleFormData = ({
  currentValue,
  currentElearningId,
  currentFrameId,
}: {
  currentValue?: FrameDataType;
  currentFrameId?: string;
  currentElearningId: string;
}) => ({
  id: currentFrameId,
  title: currentValue?.title ?? '',
  isVideoFrame: currentValue?.isVideoFrame,
  content: currentValue?.content ?? '',
  mobileContent: currentValue?.mobileContent ?? '',
  elearningId: currentElearningId,
  vocamMetadata: currentValue?.vocamMetadata ?? {
    topicId: 0,
    channelId: 0,
    regionId: 0,
    moduleId: 0,
  },
  frameMetadata: {
    ...currentValue?.frameMetadata,
    backgroundColor: currentValue?.frameMetadata?.backgroundColor ?? '',
    bgImage: currentValue?.frameMetadata?.bgImage ?? '',
    bgVideo: currentValue?.frameMetadata?.bgVideo ?? '',
    sound: currentValue?.frameMetadata?.sound ?? '',
    backgroundDisplay: currentValue?.frameMetadata?.backgroundDisplay ?? null,
  },
  bgVideoTitle: currentValue?.bgVideoTitle ?? '',
  bgVideo: currentValue?.bgVideo ?? '',
});

export const convertStringToTitleCase = (str: string): string =>
  `${str[0].toUpperCase()}${str.slice(1)}`;

export const generateQuestionAskedErrorMessage = ({
  questionAsked,
  compulsoryQuestionCount,
  questionDataCount = 0,
}: {
  questionAsked: number;
  compulsoryQuestionCount: number;
  questionDataCount?: number;
}) => {
  if (questionAsked <= 0) {
    return 'Questions asked cannot be empty.';
  }

  const isExceedingMandatory = !isCompulsoryQuestionCountEnough({
    offset: 0,
    compulsoryQuestionCount,
    questionAsked,
  });

  if (isExceedingMandatory) {
    return 'Mandatory questions exceed number of questions to ask.';
  }

  // This means that there is no questions created yet.
  if (questionDataCount === 0) {
    return 'Please click +Add button to add a new question.';
  }

  return 'Number of questions exceed the number of enabled question.';
};

export const getHTMLTextContent = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    RETURN_DOM_FRAGMENT: true,
  }).textContent ?? html;

export const reOrderOldTooltips = (
  tooltips: ElementType[],
  sourceIndex: number,
  destinationIndex: number,
) => {
  const updatedElements = Array.from(tooltips);
  const [movedElement] = updatedElements.splice(sourceIndex, 1);
  updatedElements.splice(destinationIndex, 0, movedElement);

  const reorderedElements = updatedElements.map((element, index) => ({
    ...element,
    elementMetadata: {
      ...element.elementMetadata,
      position: index + 1, // Adjusting to 1-based indexing
    },
  }));

  const updatedPositions = reorderedElements.map((element, index) => ({
    id: element.id,
    position: index + 1, // 1-based index for position
  }));

  return updatedPositions;
};
